import { useState, useEffect, useCallback, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Briefcase,
  Globe,
  FileText,
  Download,
  RefreshCw,
  Cloud,
  Link2,
  Sparkles,
} from "lucide-react";
import {
  businesses as businessesApi,
  sites,
  clusters as clustersApi,
  type Business,
  type Site,
  type Cluster,
  type CloudflareImportCandidate,
  type ClusterRecommendation,
} from "../lib/api";
import Modal from "../components/Modal";
import { usePolling } from "../hooks/use-polling";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-400",
  archived: "bg-gray-500/10 text-gray-400",
  planning: "bg-amber-500/10 text-amber-400",
};

const SITE_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-400",
  inactive: "bg-gray-500/10 text-gray-400",
  pending: "bg-amber-500/10 text-amber-400",
  building: "bg-blue-500/10 text-blue-400",
  error: "bg-red-500/10 text-red-400",
};

type PageTab = "businesses" | "import";

type Notice = {
  type: "success" | "error";
  text: string;
} | null;

function recommendationLabel(recommendation: ClusterRecommendation): string {
  if (recommendation.kind === "existing-cluster") {
    return recommendation.clusterName;
  }
  return `New cluster: ${recommendation.clusterName}`;
}

export default function Businesses() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PageTab>("businesses");
  const [businessList, setBusinessList] = useState<Business[]>([]);
  const [siteList, setSiteList] = useState<Site[]>([]);
  const [clusterList, setClusterList] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [search, setSearch] = useState("");

  // Import state
  const [importList, setImportList] = useState<CloudflareImportCandidate[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNeedsIntegration, setImportNeedsIntegration] = useState(false);
  const [importingDomain, setImportingDomain] = useState<string | null>(null);
  const [importClusterSelection, setImportClusterSelection] = useState<Record<string, string>>({});
  const [importNewClusterName, setImportNewClusterName] = useState<Record<string, string>>({});

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", niche: "", hubDomain: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [bRes, sRes, cRes] = await Promise.all([
        businessesApi.list().catch(() => ({ data: [] as Business[], total: 0 })),
        sites.list(),
        clustersApi.list(),
      ]);
      setBusinessList(bRes.data);
      setSiteList(sRes.data);
      setClusterList(cRes.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCloudflareImports = useCallback(async () => {
    setImportLoading(true);
    setImportError(null);
    try {
      const res = await sites.listCloudflareImports();
      setImportList(res.data);
      setImportNeedsIntegration(res.needsIntegration);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Failed to load Cloudflare sites");
    } finally {
      setImportLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
    void loadCloudflareImports();
  }, [loadAll, loadCloudflareImports]);

  usePolling(loadAll, 15_000);

  // Merge businesses with clusters: businesses are the primary view,
  // but fall back to clusters for groups that don't have a business record yet
  const allBusinesses: Business[] = (() => {
    const businessIds = new Set(businessList.map((b) => b._id));
    const fromClusters: Business[] = clusterList
      .filter((c) => !businessIds.has(c._id))
      .map((c) => ({ ...c, status: "active" as const }));
    return [...businessList, ...fromClusters];
  })();

  // Group sites by clusterId
  const sitesByCluster = new Map<string, Site[]>();
  const ungroupedSites: Site[] = [];
  for (const site of siteList) {
    if (site.clusterId) {
      const list = sitesByCluster.get(site.clusterId) ?? [];
      list.push(site);
      sitesByCluster.set(site.clusterId, list);
    } else {
      ungroupedSites.push(site);
    }
  }

  const filteredBusinesses = allBusinesses.filter(
    (b) =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.niche ?? "").toLowerCase().includes(search.toLowerCase()) ||
      b.hubDomain.toLowerCase().includes(search.toLowerCase())
  );

  const filteredUngrouped = ungroupedSites.filter(
    (s) =>
      s.domain.toLowerCase().includes(search.toLowerCase()) ||
      (s.config?.niche ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredImports = importList
    .filter((site) => !site.alreadyImported)
    .filter(
      (site) =>
        site.domain.toLowerCase().includes(search.toLowerCase()) ||
        site.recommendation.clusterName.toLowerCase().includes(search.toLowerCase())
    );

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await businessesApi.create({
        name: createForm.name,
        hubDomain: createForm.hubDomain,
        ...(createForm.niche ? { niche: createForm.niche } : {}),
        spokeDomains: [],
        interlinkStrategy: "hub-and-spoke",
      });
      setCreateOpen(false);
      setCreateForm({ name: "", niche: "", hubDomain: "" });
      await loadAll();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleImport(domain: string) {
    setImportingDomain(domain);
    setImportError(null);
    setNotice(null);
    try {
      const selectedClusterId = importClusterSelection[domain];
      const newClusterName = importNewClusterName[domain]?.trim();
      const res = await sites.importFromCloudflare(
        domain,
        selectedClusterId && selectedClusterId !== "auto" ? selectedClusterId : undefined,
        selectedClusterId === "new" ? newClusterName : undefined
      );
      await Promise.all([loadAll(), loadCloudflareImports()]);
      const selectedClusterName =
        selectedClusterId && selectedClusterId !== "auto"
          ? clusterList.find((cluster) => cluster._id === selectedClusterId)?.name
          : undefined;
      const createdClusterName = selectedClusterId === "new" ? newClusterName : undefined;
      const clusterMessage = selectedClusterName
        ? `added to ${selectedClusterName}`
        : createdClusterName
          ? `created in new cluster ${createdClusterName}`
          : res.recommendation.kind === "existing-cluster"
            ? `added to ${res.recommendation.clusterName}`
            : `created in new cluster ${res.recommendation.clusterName}`;
      setNotice({
        type: "success",
        text: `${domain} imported and ${clusterMessage}.`,
      });
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImportingDomain(null);
    }
  }

  function selectedImportCluster(domain: string): string {
    return importClusterSelection[domain] ?? "auto";
  }

  function selectedNewClusterName(domain: string): string {
    return importNewClusterName[domain] ?? "";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Businesses</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {allBusinesses.length} business{allBusinesses.length !== 1 ? "es" : ""}
            {" · "}{siteList.length} website{siteList.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "businesses" ? (
            <button
              onClick={() => {
                setCreateForm({ name: "", niche: "", hubDomain: "" });
                setFormError(null);
                setCreateOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Business
            </button>
          ) : (
            <button
              onClick={() => void loadCloudflareImports()}
              disabled={importLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 border border-gray-800 text-gray-200 text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${importLoading ? "animate-spin" : ""}`} />
              Refresh Cloudflare
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800">
        <button
          onClick={() => setActiveTab("businesses")}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === "businesses" ? "text-brand-400" : "text-gray-500 hover:text-gray-300"
          }`}
        >
          Businesses
          {activeTab === "businesses" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("import")}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === "import" ? "text-brand-400" : "text-gray-500 hover:text-gray-300"
          }`}
        >
          Import
          {activeTab === "import" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />
          )}
        </button>
      </div>

      {/* Notices */}
      {(error || importError || notice) && (
        <div
          className={`px-4 py-3 rounded-lg text-sm border ${
            notice?.type === "success"
              ? "bg-green-500/10 border-green-500/20 text-green-400"
              : "bg-red-500/10 border-red-500/20 text-red-400"
          }`}
        >
          {notice?.text ?? importError ?? error}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={activeTab === "businesses" ? "Search businesses..." : "Search Cloudflare sites..."}
          className="w-full pl-9 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </div>

      {/* Businesses Tab */}
      {activeTab === "businesses" ? (
        <>
          {filteredBusinesses.length === 0 && filteredUngrouped.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-12 text-center">
              <Briefcase className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {search ? "No businesses match your search" : "No businesses yet. Add your first."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBusinesses.map((biz) => {
                const bizSites = sitesByCluster.get(biz._id) ?? [];
                const totalPages = bizSites.reduce(
                  (sum, s) => sum + (s.stats?.pageCount ?? 0),
                  0
                );
                const status = biz.status ?? "active";

                return (
                  <button
                    key={biz._id}
                    onClick={() => navigate(`/businesses/${biz._id}`)}
                    className="bg-gray-900 border border-gray-800 rounded-xl p-5 text-left hover:border-gray-700 hover:bg-gray-900/80 transition-colors group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-brand-600/20 flex items-center justify-center">
                          <Briefcase className="w-4 h-4 text-brand-400" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-100 truncate group-hover:text-brand-400 transition-colors">
                            {biz.name}
                          </h3>
                          {biz.niche && (
                            <p className="text-xs text-gray-500 truncate">{biz.niche}</p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[status] ?? STATUS_COLORS["active"]}`}
                      >
                        {status}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Globe className="w-3.5 h-3.5 text-gray-600" />
                        <span className="truncate">{biz.hubDomain}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {bizSites.length} site{bizSites.length !== 1 ? "s" : ""}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {totalPages.toLocaleString()} page{totalPages !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Ungrouped sites */}
          {filteredUngrouped.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-medium text-gray-500 mb-3">Ungrouped Websites</h2>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">
                        Domain
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 hidden sm:table-cell">
                        Niche
                      </th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">
                        Status
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">
                        Pages
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUngrouped.map((site, i) => (
                      <tr
                        key={site._id}
                        onClick={() => navigate(`/websites/${site._id}`)}
                        className={`hover:bg-gray-800/40 transition-colors cursor-pointer ${
                          i < filteredUngrouped.length - 1
                            ? "border-b border-gray-800/50"
                            : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                            <span className="text-gray-200 font-medium hover:text-brand-400 transition-colors">
                              {site.domain}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs text-gray-400">
                            {site.config?.niche || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${SITE_STATUS_COLORS[site.status] ?? ""}`}
                          >
                            {site.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {site.stats?.pageCount?.toLocaleString() ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : /* Import Tab */ importNeedsIntegration ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center flex-shrink-0">
              <Cloud className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-100">
                Connect Cloudflare to import websites
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Add your Cloudflare API token and account ID in Settings. Once connected, this tab
                will automatically list every zone in that account and recommend the best business
                for each one.
              </p>
              <button
                onClick={() => navigate("/settings")}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Link2 className="w-4 h-4" />
                Open Settings
              </button>
            </div>
          </div>
        </div>
      ) : importLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredImports.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-12 text-center">
          <Cloud className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {search
              ? "No unimported Cloudflare sites match your search"
              : "No unimported Cloudflare sites available to import."}
          </p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Domain</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">
                    Import To
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 hidden xl:table-cell">
                    Role
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 hidden md:table-cell">
                    Confidence
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredImports.map((candidate, i) => (
                  <tr
                    key={candidate.zoneId}
                    className={`hover:bg-gray-800/40 transition-colors ${
                      i < filteredImports.length - 1 ? "border-b border-gray-800/50" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Globe className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-100 truncate">
                            {candidate.domain}
                          </p>
                          <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-brand-300">
                            <Sparkles className="w-3.5 h-3.5" />
                            <span className="truncate">
                              {recommendationLabel(candidate.recommendation)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {candidate.recommendation.reason}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide bg-gray-800 text-gray-400">
                        {candidate.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-2">
                        <select
                          value={selectedImportCluster(candidate.domain)}
                          onChange={(e) =>
                            setImportClusterSelection((prev) => ({
                              ...prev,
                              [candidate.domain]: e.target.value,
                            }))
                          }
                          disabled={importingDomain === candidate.domain}
                          className="w-full min-w-0 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-200 focus:border-brand-500 focus:outline-none disabled:opacity-50"
                        >
                          <option value="auto">
                            Auto ({recommendationLabel(candidate.recommendation)})
                          </option>
                          {clusterList.map((cluster) => (
                            <option key={cluster._id} value={cluster._id}>
                              {cluster.name}
                            </option>
                          ))}
                          <option value="new">New cluster...</option>
                        </select>
                        {selectedImportCluster(candidate.domain) === "new" && (
                          <input
                            type="text"
                            value={selectedNewClusterName(candidate.domain)}
                            onChange={(e) =>
                              setImportNewClusterName((prev) => ({
                                ...prev,
                                [candidate.domain]: e.target.value,
                              }))
                            }
                            placeholder="Type new cluster name"
                            disabled={importingDomain === candidate.domain}
                            className="w-full min-w-0 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-200 placeholder-gray-500 focus:border-brand-500 focus:outline-none disabled:opacity-50"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 hidden xl:table-cell">
                      {candidate.recommendation.suggestedRole}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500 hidden md:table-cell">
                      {Math.round(candidate.recommendation.confidence * 100)}%
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => void handleImport(candidate.domain)}
                        disabled={
                          importingDomain === candidate.domain ||
                          (selectedImportCluster(candidate.domain) === "new" &&
                            selectedNewClusterName(candidate.domain).trim().length < 3)
                        }
                        title={
                          importingDomain === candidate.domain
                            ? `Importing ${candidate.domain}`
                            : `Import ${candidate.domain}`
                        }
                        aria-label={
                          importingDomain === candidate.domain
                            ? `Importing ${candidate.domain}`
                            : `Import ${candidate.domain}`
                        }
                        className="inline-flex items-center justify-center w-9 h-9 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                      >
                        {importingDomain === candidate.domain ? (
                          <div className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Business Modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add Business"
        size="sm"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {formError && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Name *</label>
            <input
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              required
              placeholder="My Affiliate Business"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Niche</label>
            <input
              value={createForm.niche}
              onChange={(e) => setCreateForm((f) => ({ ...f, niche: e.target.value }))}
              placeholder="e.g. home-fitness"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Hub Domain *</label>
            <input
              value={createForm.hubDomain}
              onChange={(e) => setCreateForm((f) => ({ ...f, hubDomain: e.target.value }))}
              required
              placeholder="example.com"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {saving ? "Creating..." : "Add Business"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
